// components/features/support/tasks/TaskItem.tsx
import React from "react";
import {
  Calendar,
  Paperclip,
  User,
  Edit,
  Trash2,
  AlertCircle,
  CheckCircle2,
  Clock,
  MessageSquarePlus,
} from "lucide-react";
import { Task, TaskPriority, TaskStatus } from "../../../../types/features/support/Task";

interface TaskItemProps {
  task: Task;
  currentUserId?: string | null;
  onView?: (task: Task) => void;
  onEdit?: (task: Task) => void;
  onDelete?: (task: Task) => void;
  onAddNote?: (task: Task) => void;
}

const statusLabels: Record<TaskStatus, string> = {
  nouveau: "À démarrer",
  en_cours: "En cours",
  en_attente: "En attente",
  resolu: "Résolu",
};

const statusBarColor: Record<TaskStatus, string> = {
  nouveau: "bg-blue-500",
  en_cours: "bg-purple-500",
  en_attente: "bg-orange-500",
  resolu: "bg-green-500",
};

const statusBadge: Record<TaskStatus, string> = {
  nouveau: "bg-blue-50 text-blue-700",
  en_cours: "bg-purple-50 text-purple-700",
  en_attente: "bg-orange-50 text-orange-700",
  resolu: "bg-green-50 text-green-700",
};

const statusIcon: Record<TaskStatus, React.ComponentType<{ className?: string }>> = {
  nouveau: Clock,
  en_cours: Clock,
  en_attente: Clock,
  resolu: CheckCircle2,
};

const priorityLabels: Record<TaskPriority, string> = {
  basse: "Basse",
  moyenne: "Moyenne",
  haute: "Haute",
  critique: "Critique",
};

const priorityDot: Record<TaskPriority, string> = {
  basse: "bg-gray-400",
  moyenne: "bg-blue-500",
  haute: "bg-orange-500",
  critique: "bg-red-500",
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `il y a ${days}j`;
  const months = Math.floor(days / 30);
  if (months < 12) return `il y a ${months} mois`;
  return `il y a ${Math.floor(months / 12)} an(s)`;
}

function dueLabel(dueStr: string, status: TaskStatus): { text: string; tone: "danger" | "warn" | "ok" } {
  const due = new Date(dueStr).getTime();
  const diff = due - Date.now();
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

  if (status === "resolu") {
    return {
      text: new Date(dueStr).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "short",
      }),
      tone: "ok",
    };
  }
  if (diff < 0) {
    return { text: `En retard de ${Math.abs(days)}j`, tone: "danger" };
  }
  if (days === 0 || days === 1) return { text: "Aujourd'hui", tone: "danger" };
  if (days <= 3) return { text: `Dans ${days}j`, tone: "warn" };
  return {
    text: new Date(dueStr).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
    }),
    tone: "ok",
  };
}

export const TaskItem: React.FC<TaskItemProps> = ({ task, currentUserId, onView, onEdit, onDelete, onAddNote }) => {
  const isOverdue = task.due_date && task.status !== "resolu" && new Date(task.due_date) < new Date();

  const due = task.due_date ? dueLabel(task.due_date, task.status) : null;
  const StatusIcon = statusIcon[task.status];
  const isAssignedToMe = Boolean(currentUserId && task.agents?.some((a) => a.id === currentUserId));
  const canDelete = Boolean(currentUserId) && task.creator?.id === currentUserId;

  return (
    <div
      onClick={() => onView?.(task)}
      className="relative bg-white border border-slate-200 rounded-lg shadow-sm hover:border-slate-300 hover:shadow-md transition-all cursor-pointer overflow-hidden"
    >
      {/* Barre de statut à gauche */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${statusBarColor[task.status]}`} />

      <div className="pl-5 pr-4 py-3.5">
        {/* Ligne 1 : Titre + "Moi" */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-gray-900 truncate text-[15px]">{task.title}</h3>
              {isAssignedToMe && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium uppercase tracking-wide shrink-0">
                  Moi
                </span>
              )}
            </div>
            {task.description && <p className="text-sm text-gray-500 line-clamp-1 mt-0.5">{task.description}</p>}
          </div>
        </div>

        {/* Ligne 2 : Badges & méta */}
        <div className="flex items-center flex-wrap gap-x-3 gap-y-1.5 mt-2.5">
          <span
            className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded font-medium ${statusBadge[task.status]}`}
          >
            <StatusIcon className="w-3 h-3" />
            {statusLabels[task.status]}
          </span>

          <span className="inline-flex items-center gap-1 text-xs text-gray-600">
            <span className={`w-1.5 h-1.5 rounded-full ${priorityDot[task.priority]}`} />
            {priorityLabels[task.priority]}
          </span>

          {due && (
            <span
              className={`inline-flex items-center gap-1 text-xs font-medium ${
                due.tone === "danger" ? "text-red-600" : due.tone === "warn" ? "text-orange-600" : "text-gray-500"
              }`}
            >
              {due.tone === "danger" && <AlertCircle className="w-3 h-3" />}
              <Calendar className="w-3 h-3" />
              {due.text}
            </span>
          )}

          {task.client?.full_name && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-500 truncate max-w-[180px]">
              <User className="w-3 h-3 shrink-0" />
              <span className="truncate">{task.client.full_name}</span>
            </span>
          )}

          {task.attachments?.length > 0 && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-500">
              <Paperclip className="w-3 h-3" />
              {task.attachments.length}
            </span>
          )}

          {task.note && (
            <span className="inline-flex items-center gap-1 text-xs text-amber-600" title={task.note}>
              <MessageSquarePlus className="w-3 h-3" />
              Note
            </span>
          )}
        </div>

        {/* Ligne 3 : Assignation + auteur + ACTIONS visibles */}
        <div className="flex items-center justify-between gap-3 mt-3 pt-2.5 border-t border-gray-100">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {task.agents?.length > 0 && (
              <div className="flex -space-x-1.5 shrink-0">
                {task.agents.slice(0, 3).map((agent, idx) => (
                  <div
                    key={agent.id}
                    title={agent.name}
                    className="w-5 h-5 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-[9px] font-semibold text-gray-600"
                    style={{ zIndex: 10 - idx }}
                  >
                    {agent.name.charAt(0).toUpperCase()}
                  </div>
                ))}
                {task.agents.length > 3 && (
                  <div className="w-5 h-5 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center text-[9px] font-semibold text-gray-500">
                    +{task.agents.length - 3}
                  </div>
                )}
              </div>
            )}

            {task.creator?.name && (
              <span className="text-xs text-gray-400 truncate">
                Créé par <span className="text-gray-500">{task.creator.name.split(" ")[0]}</span>
                {" · "}
                {timeAgo(task.created_at)}
              </span>
            )}

            {isOverdue && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-red-600 shrink-0">
                <AlertCircle className="w-3 h-3" />
                En retard
              </span>
            )}
          </div>

          {/* ✅ Actions toujours visibles, avec texte */}
          <div className="flex items-center gap-1.5 shrink-0">
            {onAddNote && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddNote(task);
                }}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors"
                title={task.note ? "Modifier la note" : "Ajouter une note"}
              >
                <MessageSquarePlus className="w-3.5 h-3.5" />
                {task.note ? "Modifier la note" : "Ajouter une note"}
              </button>
            )}

            {onEdit && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(task);
                }}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
                title="Modifier la tâche"
              >
                <Edit className="w-3.5 h-3.5" />
                Modifier
              </button>
            )}

            {canDelete && onDelete && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(task);
                }}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-red-600 bg-white border border-red-200 hover:bg-red-50 transition-colors"
                title="Supprimer la tâche"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Supprimer
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
